import { router } from "@inertiajs/react"
import { X } from "lucide-react"
import { useCallback, useRef, useState } from "react"

import ContentActionMenu from "@/components/content-action-menu"
import type { StoryGroup } from "./story-row"

interface StoryViewerProps {
  storyGroups: StoryGroup[]
  initialGroupIndex: number
  onClose: () => void
}

export default function StoryViewer({
  storyGroups,
  initialGroupIndex,
  onClose,
}: StoryViewerProps) {
  const [groupIndex, setGroupIndex] = useState(initialGroupIndex)
  const [storyIndex, setStoryIndex] = useState(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const viewedRef = useRef<Set<number>>(new Set())

  const currentGroup = storyGroups[groupIndex]
  const currentStory = currentGroup.stories[storyIndex]
  const isImage = currentStory.media_type === "image"

  const recordView = useCallback((storyId: number) => {
    if (viewedRef.current.has(storyId)) return
    viewedRef.current.add(storyId)
    router.post(`/stories/${storyId}/view`, {
      preserveState: true,
      preserveScroll: true,
    })
  }, [])

  // Record view for current story
  const prevStoryIdRef = useRef<number | null>(null)
  if (currentStory.id !== prevStoryIdRef.current) {
    prevStoryIdRef.current = currentStory.id
    recordView(currentStory.id)
  }

  const advanceStory = useCallback(() => {
    if (storyIndex < currentGroup.stories.length - 1) {
      setStoryIndex((prev) => prev + 1)
    } else if (groupIndex < storyGroups.length - 1) {
      setGroupIndex((prev) => prev + 1)
      setStoryIndex(0)
    } else {
      onClose()
    }
  }, [
    storyIndex,
    currentGroup.stories.length,
    groupIndex,
    storyGroups.length,
    onClose,
  ])

  const goBack = useCallback(() => {
    if (storyIndex > 0) {
      setStoryIndex((prev) => prev - 1)
    } else if (groupIndex > 0) {
      setGroupIndex((prev) => prev - 1)
      const prevGroup = storyGroups[groupIndex - 1]
      setStoryIndex(prevGroup.stories.length - 1)
    }
  }, [storyIndex, groupIndex, storyGroups])

  // Start timer when story changes
  const prevTimerKeyRef = useRef<string | null>(null)
  const timerKey = `${groupIndex}-${storyIndex}`
  if (timerKey !== prevTimerKeyRef.current) {
    prevTimerKeyRef.current = timerKey
    if (timerRef.current) clearTimeout(timerRef.current)
    if (isImage) {
      timerRef.current = setTimeout(() => {
        advanceStory()
      }, 5000)
    }
  }

  const handleVideoEnded = useCallback(() => {
    advanceStory()
  }, [advanceStory])

  const initials = `${currentGroup.user.first_name[0]}${currentGroup.user.last_name[0]}`

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black">
      <div className="relative h-full w-full max-w-lg">
        {/* Progress bars */}
        <div className="absolute top-0 right-0 left-0 z-20 flex gap-1 px-3 pt-3">
          {currentGroup.stories.map((story, i) => (
            <div
              key={story.id}
              data-testid="story-progress-segment"
              className="h-0.5 flex-1 overflow-hidden rounded-full bg-white/30"
            >
              <div
                className={`h-full rounded-full bg-white transition-all ${
                  i < storyIndex
                    ? "w-full"
                    : i === storyIndex
                      ? "w-1/2 animate-pulse"
                      : "w-0"
                }`}
              />
            </div>
          ))}
        </div>

        {/* User info overlay */}
        <div className="absolute top-5 right-0 left-0 z-20 flex items-center gap-3 px-4 pt-2">
          {currentGroup.user.avatar_url ? (
            <img
              src={currentGroup.user.avatar_url}
              alt={currentGroup.user.full_name}
              className="h-8 w-8 rounded-full object-cover"
            />
          ) : (
            <div className="bg-krown-200 flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold text-white">
              {initials}
            </div>
          )}
          <span className="text-sm font-semibold text-white drop-shadow-md">
            {currentGroup.user.full_name}
          </span>

          <div className="ml-auto flex items-center gap-1">
            <ContentActionMenu
              reportableType="Story"
              reportableId={currentStory.id}
              showBlock
              blockUserId={currentGroup.user.id}
            />
            <button
              onClick={onClose}
              aria-label="Cerrar"
              className="rounded-full p-1 text-white hover:bg-white/20"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Story media */}
        <div className="flex h-full items-center justify-center">
          {isImage ? (
            <img
              src={currentStory.media_url ?? ""}
              alt={currentStory.caption ?? "Historia"}
              className="h-full w-full object-contain"
            />
          ) : (
            <video
              key={currentStory.id}
              src={currentStory.media_url ?? ""}
              className="h-full w-full object-contain"
              autoPlay
              playsInline
              onEnded={handleVideoEnded}
            />
          )}
        </div>

        {/* Tap zones */}
        <div
          data-testid="story-tap-left"
          onClick={goBack}
          className="absolute top-0 bottom-0 left-0 z-10 w-1/3 cursor-pointer"
        />
        <div
          data-testid="story-tap-right"
          onClick={advanceStory}
          className="absolute top-0 right-0 bottom-0 z-10 w-1/3 cursor-pointer"
        />

        {/* Caption */}
        {currentStory.caption && (
          <div className="absolute right-0 bottom-0 left-0 z-20 bg-gradient-to-t from-black/60 to-transparent px-4 pt-16 pb-8">
            <p className="text-sm text-white drop-shadow-md">
              {currentStory.caption}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
